// Routers.js
import React, { Suspense } from "react";
import { HashRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import {
  appPath,
  APP_PATHS,
  APP_BASE,
  stripLegacyAppFromPath,
  stripThemeLayoutSuffix,
  toRoutePath,
  getPostLoginPath,
} from "../utils/appPaths";
import Loader from "../Layout/Loader";
import ErrorBoundary from "../Components/ErrorBoundary";
import { authRoutes } from "./AuthRoutes";
import AppLayout from "../Layout/Layout";
import Error503 from "../Components/Pages/ErrorPages/ErrorPage503";
import PrivateRoute from "./PrivateRoute";
import RoleGate from "./RoleGate";
import { routes } from "./Routes";
import { useAuth } from "../_helper/Auth/AuthProvider";
import AuthService from "../Services/auth/AuthService";

const LegacyAppRedirect = () => {
  const location = useLocation();
  const nextPath = stripLegacyAppFromPath(location.pathname);
  return (
    <Navigate
      to={`${nextPath}${location.search}${location.hash}`}
      replace
    />
  );
};

/** Redirect theme layout URLs or show 503 inside the app shell. */
const AppCatchAll = () => {
  const location = useLocation();
  const normalized = stripLegacyAppFromPath(location.pathname);
  const withoutLayout = stripThemeLayoutSuffix(normalized);
  if (withoutLayout !== normalized) {
    return (
      <Navigate
        to={`${withoutLayout}${location.search}${location.hash}`}
        replace
      />
    );
  }
  return <Error503 />;
};

const AuthAuthedRedirect = () => {
  const location = useLocation();
  return <Navigate to={getPostLoginPath(location)} replace />;
};

const Routers = () => {
  const { authenticated, isLoggedIn } = useAuth() || {};

  // HashRouter: Hostinger CDN returns 404 for /login on refresh; hash URLs
  // only request "/" so the SPA always loads without nginx/.htaccess rewrites.
  return (
    <HashRouter basename={APP_BASE || "/"}>
      <ErrorBoundary>
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/app/*" element={<LegacyAppRedirect />} />

          {authRoutes.map(({ path, Component }, i) => {
            const isPublicWhenAuthed =
              path === appPath("/verify-email/:token") ||
              path === appPath("/forget-pwd") ||
              path === appPath("/reset-pwd");
            return (
              <Route
                path={path}
                element={
                  path === appPath("/login/otp-verif/:param") &&
                  !isLoggedIn &&
                  !AuthService.hasPendingOtp() &&
                  !AuthService.isAuthenticated() ? (
                    <Navigate to={APP_PATHS.login} replace />
                  ) : authenticated && !isPublicWhenAuthed ? (
                    <AuthAuthedRedirect />
                  ) : (
                    <Component />
                  )
                }
                key={i}
              />
            );
          })}

          <Route path="/*" element={<PrivateRoute />}>
            <Route element={<AppLayout />}>
              <Route
                index
                element={<Navigate to={toRoutePath(APP_PATHS.dashboard)} replace />}
              />
              {routes.map(({ path, Component, roles }, i) => (
                <Route
                  key={i}
                  path={toRoutePath(path)}
                  element={<RoleGate roles={roles}>{Component}</RoleGate>}
                />
              ))}
              <Route path="*" element={<AppCatchAll />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </HashRouter>
  );
};

export default Routers;
